import { useCallback, type ReactNode } from 'react';

import { formatDate, formatTime } from '@/shared/format';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The tenant's RECENT ACTIVITY — its stock movements.
 *
 * ---------------------------------------------------------------------------
 * LABELLED FOR WHAT IT IS
 * ---------------------------------------------------------------------------
 * The heading says "Recent stock movements", not "Recent activity". The platform has several
 * kinds of activity — orders, bookings, messages, and more that are not implemented — and this
 * widget shows ONE of them. A heading that claims more than the widget delivers is how an
 * operator comes to believe that nothing happened because nothing was listed.
 *
 * The movements are the newest first, and each carries its type, its quantity and when it
 * happened.
 */
export function RecentActivityWidget({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.stockMovements(), []);

  return (
    <DashboardWidgetFrame
      title="Recent stock movements"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No stock movements yet"
      emptyDescription="A movement is recorded whenever stock is received, moved or adjusted."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <StatCard
            label="Movements recorded"
            value={String(data.total)}
            caption="Newest first"
          />
          <ul className="flex flex-col">
            {data.movements.slice(0, 10).map((movement) => (
              <li
                key={movement.id}
                className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="text-content">{movement.movementType}</span>
                  {movement.reason === null ? null : (
                    <span className="truncate text-xs text-content-muted">{movement.reason}</span>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end">
                  <span className="text-content">
                    {movement.quantity} {movement.unitOfMeasure}
                  </span>
                  <span className="text-xs text-content-muted">
                    {formatDate(movement.createdAt)}, {formatTime(movement.createdAt)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
